export type PatternRunner = (pattern: string, text: string) => Promise<boolean>;

export interface PatternWorker {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage: (message: unknown) => void;
  terminate: () => void;
}

export const PATTERN_TIMEOUT_MS = 50;

interface WorkerPatternRunnerOptions {
  timeoutMs?: number;
  spawnWorker?: () => PatternWorker;
}

function spawnPatternWorker(): PatternWorker {
  return new Worker(new URL('./pattern.worker.ts', import.meta.url), { type: 'module' });
}

/**
 * RegExp.test cannot be interrupted, and an author pattern such as ^(a+)+$ freezes the tab on
 * every keystroke. The test runs in a worker that is killed after the timeout, and a timeout
 * counts as a failed match, the answer the server's guarded test gives.
 */
export function createWorkerPatternRunner(options: WorkerPatternRunnerOptions = {}): PatternRunner {
  const timeoutMs = options.timeoutMs ?? PATTERN_TIMEOUT_MS;
  const spawn = options.spawnWorker ?? spawnPatternWorker;
  let ready: Promise<PatternWorker | null> | null = null;
  let queue: Promise<unknown> = Promise.resolve();

  // The timeout starts only once the worker has loaded, so a slow first load is not a timeout.
  function readyWorker(): Promise<PatternWorker | null> {
    ready ??= new Promise((resolve) => {
      let worker: PatternWorker;
      try {
        worker = spawn();
      } catch {
        resolve(null);
        return;
      }
      worker.onmessage = (event) => {
        if (event.data === 'ready') resolve(worker);
      };
      worker.onerror = () => {
        worker.terminate();
        resolve(null);
      };
    });
    return ready;
  }

  async function runOne(pattern: string, text: string): Promise<boolean> {
    const worker = await readyWorker();
    // Without a worker there is no safe way to run the pattern here; the server still checks it.
    if (!worker) return true;

    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        worker.terminate();
        ready = null;
        resolve(false);
      }, timeoutMs);
      worker.onmessage = (event) => {
        clearTimeout(timer);
        resolve(event.data !== false);
      };
      worker.postMessage({ pattern, text });
    });
  }

  // One test at a time: a timeout kills the worker, and with it anything queued inside it.
  return (pattern, text) => {
    const job = queue.then(() => runOne(pattern, text));
    queue = job.catch(() => undefined);
    return job;
  };
}
